import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {ReactNode} from 'react';

import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {ChannelTypesEnum} from '../../models/api/channelCommonTypes';
import {ChannelModel} from '../../models/api/channelModel';
import {ChannelProvidersEnum} from '../../models/api/channelPreferencesModel';
import {CompanyPreferencesModel} from '../../models/api/companyPreferencesModel';

const channelDeniedErrors = [
  'denied',
  'no_read_page_mailboxes_perm',
  'app_perm_revoked',
  'no_manage_pages_perm',
  'facebook_perm',
];

export function isChannelDenied(channel: ChannelModel) {
  if (!channel.error?.type) {
    return false;
  }

  return channelDeniedErrors.includes(channel.error?.type);
}

export enum ChannelWarningsEnum {
  SLOW_LANE = 'slow_lane',
  NEEDS_VALIDATION = 'needs_validation',
  DENIED_SMTP = 'denied_smtp',
  DENIED_OTHER = 'denied_other',
  O365_INVALID_FOLDERS = 'o365_invalid_folders',
  O365_SEND_DENIED = 'o365_send_denied',
  O365_ACCESS_DENIED = 'o365_access_denied',
  O365_NOT_SUPPORTED_ACCOUNT = 'o365_not_supported_account',
  GMAIL_SERVICE_NOT_ENABLED = 'gmail_service_not_enabled',
  GMAIL_CREDENTIALS = 'gmail_credentials',
  CALENDAR_CREDENTIALS = 'calendar_credentials',
  SPF_DKIM_WARNING = 'spf_dkim_warning',
}

const isChannelWarning = makeEnumTypeGuard(ChannelWarningsEnum);

const smtpDnsProviders = [ChannelProvidersEnum.MANDRILL, ChannelProvidersEnum.SENDGRID];

export function getWarningsForChannel(
  channel: ChannelModel,
  userNamespace: string,
  companyPreferences: CompanyPreferencesModel,
): ReadonlyArray<ChannelWarningsEnum> {
  const isDenied =
    isChannelDenied(channel) ||
    (channel.type === ChannelTypesEnum.OFFICE365 && channel.error?.type === 'office365_webhook');

  const hasSpfDkimWarning = !companyPreferences.skipSpfDkimWarnings && channel.behaviors.hasSpfDkimWarning;
  const isSharedChannel = isTeamNamespace(channel.namespace) && channel.namespace !== userNamespace;
  const hasSmtpProvider =
    channel.type === ChannelTypesEnum.SMTP &&
    channel.preferences.provider &&
    smtpDnsProviders.includes(channel.preferences.provider);

  const showDeliverabilityWarning =
    hasSpfDkimWarning && isSharedChannel && !channel.preferences.isDemo && hasSmtpProvider;

  const conditionsMap: Record<ChannelWarningsEnum, boolean> = {
    [ChannelWarningsEnum.SLOW_LANE]: channel.error?.type === 'slow_lane',
    [ChannelWarningsEnum.NEEDS_VALIDATION]:
      channel.type === ChannelTypesEnum.SMTP && !channel.preferences.canSend,
    [ChannelWarningsEnum.DENIED_SMTP]: channel.type === ChannelTypesEnum.SMTP && isDenied,
    [ChannelWarningsEnum.DENIED_OTHER]: channel.type !== ChannelTypesEnum.SMTP && isDenied,
    [ChannelWarningsEnum.O365_INVALID_FOLDERS]:
      channel.type === ChannelTypesEnum.OFFICE365 && channel.error?.type === 'invalid_folders_mapping',
    [ChannelWarningsEnum.O365_SEND_DENIED]:
      channel.type === ChannelTypesEnum.OFFICE365 && channel.error?.type === 'missing_o365_send_permissions',
    [ChannelWarningsEnum.O365_ACCESS_DENIED]:
      channel.type === ChannelTypesEnum.OFFICE365 &&
      channel.error?.type === 'missing_o365_access_permissions',
    [ChannelWarningsEnum.O365_NOT_SUPPORTED_ACCOUNT]:
      channel.type === ChannelTypesEnum.OFFICE365 && channel.error?.type === 'account_not_supported',
    [ChannelWarningsEnum.GMAIL_SERVICE_NOT_ENABLED]:
      channel.type === ChannelTypesEnum.GMAIL && channel.error?.type === 'service_not_enabled',
    [ChannelWarningsEnum.GMAIL_CREDENTIALS]:
      channel.type === ChannelTypesEnum.GMAIL && channel.error?.type === 'gmail_credentials',
    [ChannelWarningsEnum.CALENDAR_CREDENTIALS]:
      (channel.type === ChannelTypesEnum.GMAIL || channel.type === ChannelTypesEnum.OFFICE365) &&
      channel.behaviors.isMissingCalendarScopes,
    [ChannelWarningsEnum.SPF_DKIM_WARNING]: Boolean(showDeliverabilityWarning),
  };

  const positiveKeys = _(conditionsMap)
    .entries()
    .filter(([, value]) => value)
    .map(([key]) => key)
    .filter(isChannelWarning)
    .value();

  return positiveKeys;
}

export function getWarningProps(warning: ChannelWarningsEnum, isUserGlobalAdmin: boolean) {
  const warningPropsMap: Record<
    ChannelWarningsEnum,
    {
      label: ReactNode;
      details: ReactNode;
      link?: {
        label: ReactNode;
        url: string;
      };
      ctaLabel?: ReactNode;
    }
  > = {
    [ChannelWarningsEnum.SLOW_LANE]: {
      label: (
        <Trans id="channelwarning-slow_lane-label">
          This channel is currently receiving too much traffic.
        </Trans>
      ),
      details: (
        <Trans id="channelwarning-slow_lane-details">
          New messages will be delayed until the traffic decreases.
        </Trans>
      ),
      ctaLabel: <Trans id="channelwarning-slow_lane-ctalabel">Contact support</Trans>,
    },
    [ChannelWarningsEnum.NEEDS_VALIDATION]: {
      label: <Trans id="channelwarning-needsvalidation-label">This address needs to be validated.</Trans>,
      details: (
        <Trans id="channelwarning-needsvalidation-details">
          We&apos;ll send an email to check if your redirection is working.
        </Trans>
      ),
      link: {
        label: <Trans id="channelwarning-needsvalidation-linklabel">Why?</Trans>,
        url: 'https://help.frontapp.com/t/8029mr',
      },
      ctaLabel: <Trans id="channelwarning-needsvalidation-ctalabel">Send email</Trans>,
    },
    [ChannelWarningsEnum.O365_SEND_DENIED]: {
      label: (
        <Trans id="channelwarning-o365_send_denied-label">You need to update your O365 permissions</Trans>
      ),
      details: (
        <Trans id="channelwarning-o365_send_denied-details">
          We have detected that you need to update O365 permissions to send messages.
        </Trans>
      ),
      link: {
        label: <Trans id="channelwarning-o365_send_denied-linklabel">Learn more</Trans>,
        url: 'https://help.frontapp.com/t/35h3dvh',
      },
      ctaLabel: <Trans id="channelwarning-o365_send_denied-ctalabel">Verify permissions</Trans>,
    },
    [ChannelWarningsEnum.O365_ACCESS_DENIED]: {
      label: (
        <Trans id="channelwarning-o365_access_denied-label">You need to update your O365 permissions</Trans>
      ),
      details: (
        <Trans id="channelwarning-o365_access_denied-details">
          We have detected that you need to update O365 permissions to access messages.
        </Trans>
      ),
      link: {
        label: <Trans id="channelwarning-o365_access_denied-linklabel">Learn more</Trans>,
        url: 'https://help.frontapp.com/t/35h3dvh',
      },
      ctaLabel: <Trans id="channelwarning-o365_access_denied-ctalabel">Reauthorize</Trans>,
    },
    [ChannelWarningsEnum.DENIED_SMTP]: {
      label: (
        <Trans id="channelwarning-denied_smtp-label">This channel is experiencing suspicious activity.</Trans>
      ),
      details: (
        <Trans id="channelwarning-denied_smtp-details">
          New messages will not be imported. Please contact our support.
        </Trans>
      ),
    },
    [ChannelWarningsEnum.DENIED_OTHER]: {
      label: <Trans id="channelwarning-denied_other-label">Our access to this account was revoked.</Trans>,
      details: (
        <Trans id="channelwarning-denied_other-details">
          New messages will not be imported until you reauthorize.
        </Trans>
      ),
      ctaLabel: <Trans id="channelwarning-denied_other-ctaLabel">Reauthorize</Trans>,
    },
    [ChannelWarningsEnum.O365_INVALID_FOLDERS]: {
      label: (
        <Trans id="channelwarning-o365_invalid_folders-label">
          Your account is missing some required folders.
        </Trans>
      ),
      details: <Trans id="channelwarning-o365_invalid_folders-details">Need help?</Trans>,
      link: {
        label: <Trans id="channelwarning-o365_invalid_folders-linklabel">Learn more</Trans>,
        url: 'https://help.frontapp.com/t/m2ml0m/how-to-add-an-office-365-inbox-to-front',
      },
      ctaLabel: <Trans id="channelwarning-o365_invalid_folders-ctaLabel">Reconnect now</Trans>,
    },
    [ChannelWarningsEnum.O365_NOT_SUPPORTED_ACCOUNT]: {
      label: (
        <Trans id="channelwarning-o365_not_supported_account-label">
          Your Office 365 account is currently not supported.
        </Trans>
      ),
      details: <Trans id="channelwarning-o365_not_supported_account-details">Need help?</Trans>,
      link: {
        label: <Trans id="channelwarning-o365_not_supported_account-linklabel">Learn more</Trans>,
        url: 'https://help.frontapp.com/t/m2ml0m/how-to-add-an-office-365-inbox-to-front',
      },
    },
    [ChannelWarningsEnum.GMAIL_SERVICE_NOT_ENABLED]: {
      label: (
        <Trans id="channelwarning-gmail_service_not_enabled-label">
          Mail service is not enabled on your account.
        </Trans>
      ),
      details: (
        <Trans id="channelwarning-gmail_service_not_enabled-details">
          Please contact your administrator to enable the mail service on this Google account.
        </Trans>
      ),
      ctaLabel: <Trans id="channelwarning-gmail_service_not_enabled-ctaLabel">Reauthorize</Trans>,
    },
    [ChannelWarningsEnum.GMAIL_CREDENTIALS]: {
      label: (
        <Trans id="channelwarning-gmail_credentials-label">Our access to this account was revoked.</Trans>
      ),
      details: (
        <Trans id="channelwarning-gmail_credentials-details">
          New messages will not be imported until you reauthorize.
        </Trans>
      ),
      ctaLabel: <Trans id="channelwarning-gmail_credentials-ctaLabel">Reauthorize</Trans>,
    },
    [ChannelWarningsEnum.CALENDAR_CREDENTIALS]: {
      label: (
        <Trans id="channelwarning-calendar_credentials-label">
          This account is missing calendar permissions.
        </Trans>
      ),
      details: (
        <Trans id="channelwarning-calendar_credentials-details">
          Please reauthorize to enable calendar features.
        </Trans>
      ),
      ctaLabel: <Trans id="channelwarning-calendar_credentials-ctaLabel">Reauthorize</Trans>,
    },
    [ChannelWarningsEnum.SPF_DKIM_WARNING]: {
      label: <Trans id="channelwarning-spf_dkim_warning-label">SPF/DKIM records missing</Trans>,
      details: (
        <Trans id="channelwarning-spf_dkim_warning-details">
          You need to set SPF/DKIM records to ensure emails are delivered properly.
        </Trans>
      ),
      ctaLabel: isUserGlobalAdmin ? (
        <Trans id="channelwarning-spf_dkim_warning-viewsettings">View settings</Trans>
      ) : (
        <Trans id="channelwarning-spf_dkim_warning-getithelp">Get IT help</Trans>
      ),
    },
  };

  return warningPropsMap[warning];
}
